import {
    CreatePeriodRequestDto,
    PeriodByIdRequestDto,
    PeriodsByUserRequestDto,
    PeriodsResponseDto,
    UpdatePeriodRequestDto,
} from '../interfaces/period';
import { request } from './Request';

/**
 * Servicio para manejar operaciones CRUD de periodos menstruales
 * Utiliza la clase Request para comunicarse con la API
 */
class PeriodService {
    private readonly baseEndpoint = '/periodos';

    /**
     * Obtiene todos los periodos de un usuario específico
     * @param payload - Datos del usuario para filtrar periodos
     * @returns Lista de periodos del usuario
     */
    async getPeriodsByUser(payload: PeriodsByUserRequestDto): Promise<PeriodsResponseDto> {
        try {
            const response = await request.post<PeriodsResponseDto>(
                `${this.baseEndpoint}/findAll`,
                payload
            );
            return response.data;
        } catch (error) {
            console.error('Error al obtener periodos del usuario:', error);
            throw new Error('No se pudieron obtener los periodos del usuario');
        }
    }

    /**
     * Obtiene un periodo específico por su ID
     * @param payload - Datos con el ID del periodo a obtener
     * @returns Datos del periodo
     */
    async getPeriodById(payload: PeriodByIdRequestDto): Promise<PeriodsResponseDto> {
        try {
            const response = await request.post<PeriodsResponseDto>(
                `${this.baseEndpoint}/findById`,
                payload
            );
            return response.data;
        } catch (error) {
            console.error('Error al obtener el periodo:', error);
            throw new Error('No se pudo obtener el periodo');
        }
    }

    /**
     * Crea un nuevo periodo menstrual
     * @param payload - Datos del nuevo periodo
     * @returns periodo creado
     */
    async createPeriod(payload: CreatePeriodRequestDto): Promise<PeriodsResponseDto> {
        try {
            const response = await request.post<PeriodsResponseDto>(
                `${this.baseEndpoint}/create`,
                payload
            );

            return response.data;
        } catch (error) {
            console.error('Error al crear el periodo:', error);
            throw new Error('No se pudo crear el periodo');
        }
    }

    /**
     * Actualiza parcialmente un periodo existente
     * @param payload - Datos parciales para actualizar el periodo
     * @returns periodo actualizado
     */
    async updatePeriod(payload: UpdatePeriodRequestDto): Promise<PeriodsResponseDto> {
        try {
            const response = await request.patch<PeriodsResponseDto>(
                `${this.baseEndpoint}/update`,
                payload
            );

            return response.data;
        } catch (error) {
            console.error('Error al actualizar parcialmente el periodo:', error);
            throw new Error('No se pudo actualizar el periodo');
        }
    }

    /**
     * Elimina un periodo por su ID
     * @param payload - Datos con el ID del periodo a eliminar
     * @returns Confirmación de eliminación
     */
    async deletePeriod(payload: PeriodByIdRequestDto): Promise<PeriodsResponseDto> {
        try {
            const response = await request.delete<PeriodsResponseDto>(
                `${this.baseEndpoint}/remove`,
                payload
            );

            return response.data;
        } catch (error) {
            console.error('Error al eliminar el periodo:', error);
            throw new Error('No se pudo eliminar el periodo');
        }
    }
}

// Exportar instancia singleton
export const periodService = new PeriodService();

// Exportar la clase para crear instancias personalizadas si es necesario
export default PeriodService;